import Container from "react-bootstrap/Container";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { Store } from "../Store";
import { toast } from "react-toastify";
import { getError } from "../utils";
import { api } from "../Api";

const DEFAULT_AVATAR = "https://i.postimg.cc/bryMmCQB/profile-image.jpg";

/** Small live status pill shown under a field: idle | checking | ok | bad. */
function FieldStatus({ state, okText, badText }) {
  if (state === "checking") {
    return (
      <div className="au-status au-status-checking">
        <span className="au-spinner-sm" /> Checking…
      </div>
    );
  }
  if (state === "ok") {
    return (
      <div className="au-status au-status-ok">
        <i className="fa fa-check-circle" aria-hidden="true" /> {okText}
      </div>
    );
  }
  if (state === "bad") {
    return (
      <div className="au-status au-status-bad">
        <i className="fa fa-exclamation-circle" aria-hidden="true" /> {badText}
      </div>
    );
  }
  return null;
}

/** Live preview card for a resolved user (reference / placement). */
function UserPreview({ state, user }) {
  if (state === "checking") {
    return (
      <div className="au-preview au-preview-loading">
        <span className="au-spinner-sm" /> Looking up user…
      </div>
    );
  }
  if (state === "ok" && user) {
    return (
      <div className="au-preview au-preview-ok">
        <img src={user.profilePhoto || DEFAULT_AVATAR} alt="" className="au-preview-avatar" />
        <div className="au-preview-info">
          <div className="au-preview-name">{user.name}</div>
          <div className="au-preview-username">@{user.username}</div>
        </div>
        <i className="fa fa-check-circle au-preview-badge" aria-hidden="true" />
      </div>
    );
  }
  if (state === "bad") {
    return (
      <div className="au-preview au-preview-bad">
        <i className="fa fa-exclamation-circle" aria-hidden="true" /> User not found
      </div>
    );
  }
  return null;
}

export default function SignupScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const me = userInfo.username;

  const values = useLocation();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [reference, setReference] = useState(userInfo.username || "");
  const [placement] = useState(values.state.placement || "");
  const [password, setPassword] = useState("");
  const [position, setPosition] = useState(values.state.position || "0");
  const [username, setUsername] = useState("");
  const [gender, setGender] = useState("0");
  const [nid, setNid] = useState("");

  // idle | checking | ok | bad
  const [usernameStatus, setUsernameStatus] = useState("idle");
  const [referenceStatus, setReferenceStatus] = useState("idle");
  const [referenceUser, setReferenceUser] = useState(null);
  const [placementStatus, setPlacementStatus] = useState("idle");
  const [placementUser, setPlacementUser] = useState(null);
  const [positionStatus, setPositionStatus] = useState("idle");
  const [positionMessage, setPositionMessage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  // null while idle, "working" during the POST, "done" once the server has
  // confirmed. Drives the overlay below.
  const [submitPhase, setSubmitPhase] = useState(null);
  const [submitStep, setSubmitStep] = useState(0);

  const authHeader = { headers: { Authorization: `Bearer ${userInfo.token}` } };

  useEffect(() => {
    if (!userInfo) navigate("/login");
  }, [navigate, userInfo]);

  /* --------------------------------------------------- live username check */

  useEffect(() => {
    const value = username.trim();
    if (!value) {
      setUsernameStatus("idle");
      return;
    }
    setUsernameStatus("checking");
    const timer = setTimeout(async () => {
      try {
        await Axios.post(api + "/api/users/checkusername", { value });
        setUsernameStatus("ok");
      } catch (error) {
        setUsernameStatus("bad");
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [username]);

  /* ------------------------------------------- live reference user preview */

  useEffect(() => {
    const value = reference.trim();
    if (!value) {
      setReferenceStatus("idle");
      setReferenceUser(null);
      return;
    }
    setReferenceStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const { data } = await Axios.post(
          api + "/api/users/lookup-username",
          { value },
          authHeader,
        );
        setReferenceUser(data);
        setReferenceStatus("ok");
      } catch (error) {
        setReferenceUser(null);
        setReferenceStatus("bad");
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  /* ------------------------------------------- placement user preview (fixed) */

  useEffect(() => {
    const value = String(placement || "").trim();
    if (!value) {
      setPlacementStatus("idle");
      return;
    }
    setPlacementStatus("checking");
    (async () => {
      try {
        const { data } = await Axios.post(
          api + "/api/users/lookup-username",
          { value },
          authHeader,
        );
        setPlacementUser(data);
        setPlacementStatus("ok");
      } catch (error) {
        setPlacementStatus("bad");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement]);

  /* --------------------------------------------------------------- helpers */

  const checkPosition = async (value) => {
    setPosition(value);
    setPositionStatus("checking");
    setPositionMessage("");
    try {
      const { data } = await Axios.post(api + "/api/users/checkposition", {
        reference,
        placement,
        value,
      });
      setPositionStatus("ok");
      setPositionMessage(data.message || "Position available.");
    } catch (error) {
      setPosition("0");
      setPositionStatus("bad");
      setPositionMessage(getError(error));
    }
  };

  /* ----------------------------------------------- submit progress ticker */

  // The request itself is normally well under a second; these steps exist so
  // the overlay reads as progress instead of a frozen spinner when the server
  // or the connection is having a slow moment. The last step is sticky — we
  // never claim a stage the server hasn't confirmed.
  const SUBMIT_STEPS = [
    "Creating the account…",
    "Charging the reference point…",
    "Calculating matching bonus…",
    "Finishing up…",
  ];

  useEffect(() => {
    if (submitPhase !== "working") return;
    const timer = setInterval(() => {
      setSubmitStep((n) => (n < SUBMIT_STEPS.length - 1 ? n + 1 : n));
    }, 650);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitPhase]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (usernameStatus === "bad") {
      toast.error("This username is already taken.");
      return;
    }
    if (name.length < 6 || name.length > 30) {
      toast.error("Please Enter Valid Full Name");
      return;
    }
    if (phone.length < 10 || phone.length > 15) {
      toast.error("Please Enter a Valid Phone Number");
      return;
    }
    if (!reference) {
      toast.error("Valid Reference is Required");
      return;
    }
    if (!placement) {
      toast.error("Valid Placement is Required");
      return;
    }
    if (position === "0") {
      toast.error("Please Select the available position.");
      return;
    }
    if (password.length < 4 || password.length > 30) {
      toast.error("Please Enter a Strong Password");
      return;
    }

    setIsSubmitting(true);
    setSubmitStep(0);
    setSubmitPhase("working");
    try {
      const { data } = await Axios.post(
        api + "/api/users/adduser",
        {
          name,
          username,
          phone,
          email,
          nid,
          gender,
          reference,
          placement,
          position,
          password,
          me,
        },
        { headers: { Authorization: `Bearer ${userInfo.token}` } },
      );
      setSubmitPhase("done");
      toast.success(data.message);
      // Let the success tick land before the screen changes. The navigation
      // target is unchanged.
      setTimeout(() => navigate("/treeview?placement=" + btoa(placement)), 750);
    } catch (err) {
      setSubmitPhase(null);
      toast.error(getError(err));
      setIsSubmitting(false);
    }
  };

  function gotoHome() {
    navigate("/treeview");
  }

  const usernameInputCls =
    usernameStatus === "bad"
      ? "form-control au-input-bad au-shake"
      : usernameStatus === "ok"
      ? "form-control au-input-ok"
      : "form-control";

  return (
    <div className="au-page">
      <Helmet>
        <title>Add User | ClickCart International Ltd.</title>
      </Helmet>
      <Container className="small-container">
        <div className="au-card">
          <div className="au-header" onClick={gotoHome}>
            <i className="fa fa-long-arrow-left" aria-hidden="true" />
            <span>Add New Team Member</span>
          </div>

          <form onSubmit={submitHandler} className="au-form">
            {/* ---------------------------------------------------- personal */}
            <div className="au-section">
              <div className="au-section-title">
                <i className="fa fa-user-circle" aria-hidden="true" /> Personal Information
              </div>

              <div className="au-field">
                <label className="au-label">Full Name</label>
                <div className="au-input-group">
                  <span className="au-input-icon">
                    <i className="fa fa-id-card-o" aria-hidden="true" />
                  </span>
                  <input
                    type="text"
                    onChange={(e) => setName(e.target.value)}
                    value={name}
                    required
                    className="form-control"
                    placeholder="Full Name"
                  />
                </div>
              </div>

              <div className="au-field">
                <label className="au-label">Username</label>
                <div className="au-input-group">
                  <span className="au-input-icon">
                    <i className="fa fa-at" aria-hidden="true" />
                  </span>
                  <input
                    type="text"
                    onChange={(e) => setUsername(e.target.value.toLowerCase().trim())}
                    maxLength={20}
                    value={username}
                    required
                    className={usernameInputCls}
                    placeholder="Choose a username"
                  />
                  {usernameStatus === "checking" && <span className="au-spinner-sm au-input-trailing" />}
                  {usernameStatus === "ok" && (
                    <i className="fa fa-check-circle au-input-trailing au-ok-pop" aria-hidden="true" />
                  )}
                  {usernameStatus === "bad" && (
                    <i className="fa fa-times-circle au-input-trailing au-bad-pop" aria-hidden="true" />
                  )}
                </div>
                <FieldStatus
                  state={usernameStatus === "checking" ? "checking" : usernameStatus === "ok" ? "ok" : usernameStatus === "bad" ? "bad" : "idle"}
                  okText="Username is available"
                  badText="Username already taken"
                />
              </div>

              <div className="au-field-row">
                <div className="au-field">
                  <label className="au-label">Phone</label>
                  <div className="au-input-group">
                    <span className="au-input-icon">
                      <i className="fa fa-phone" aria-hidden="true" />
                    </span>
                    <input
                      type="text"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="form-control"
                      placeholder="Phone"
                    />
                  </div>
                </div>
                <div className="au-field">
                  <label className="au-label">Email (optional)</label>
                  <div className="au-input-group">
                    <span className="au-input-icon">
                      <i className="fa fa-envelope" aria-hidden="true" />
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                      className="form-control"
                      placeholder="Email"
                    />
                  </div>
                </div>
              </div>

              <div className="au-field-row">
                <div className="au-field">
                  <label className="au-label">NID (optional)</label>
                  <div className="au-input-group">
                    <span className="au-input-icon">
                      <i className="fa fa-id-badge" aria-hidden="true" />
                    </span>
                    <input
                      type="number"
                      value={nid}
                      onChange={(e) => setNid(e.target.value.trim())}
                      className="form-control"
                      placeholder="NID"
                    />
                  </div>
                </div>
                <div className="au-field">
                  <label className="au-label">Gender</label>
                  <div className="au-input-group">
                    <span className="au-input-icon">
                      <i className="fa fa-venus-mars" aria-hidden="true" />
                    </span>
                    <select
                      className="form-control"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      required
                    >
                      <option value="0">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* --------------------------------------------------- placement */}
            <div className="au-section">
              <div className="au-section-title">
                <i className="fa fa-sitemap" aria-hidden="true" /> Placement Details
              </div>

              <div className="au-field">
                <label className="au-label">Reference</label>
                <div className="au-input-group">
                  <span className="au-input-icon">
                    <i className="fa fa-address-book" aria-hidden="true" />
                  </span>
                  <input
                    type="text"
                    value={reference}
                    required
                    className="form-control"
                    placeholder="Reference username"
                    onChange={(e) => setReference(e.target.value.toLowerCase().trim())}
                  />
                </div>
                <UserPreview state={referenceStatus} user={referenceUser} />
              </div>

              <div className="au-field">
                <label className="au-label">Placement</label>
                <div className="au-input-group">
                  <span className="au-input-icon">
                    <i className="fa fa-id-card" aria-hidden="true" />
                  </span>
                  <input
                    type="text"
                    placeholder="Placement ID"
                    value={placement}
                    readOnly
                    className="form-control au-input-readonly"
                    required
                  />
                </div>
                <UserPreview state={placementStatus} user={placementUser} />
              </div>

              <div className="au-field">
                <label className="au-label">Position</label>
                <div className="au-input-group">
                  <span className="au-input-icon">
                    <i className="fa fa-anchor" aria-hidden="true" />
                  </span>
                  <select
                    className="form-control"
                    value={position}
                    onChange={(e) => checkPosition(e.target.value)}
                    required
                  >
                    <option value="0">Select Position</option>
                    <option value="right">Right</option>
                    <option value="left">Left</option>
                  </select>
                  {positionStatus === "checking" && <span className="au-spinner-sm au-input-trailing" />}
                </div>
                <FieldStatus
                  state={positionStatus}
                  okText={positionMessage}
                  badText={positionMessage}
                />
              </div>
            </div>

            {/* ---------------------------------------------------- security */}
            <div className="au-section">
              <div className="au-section-title">
                <i className="fa fa-lock" aria-hidden="true" /> Security
              </div>
              <div className="au-field">
                <label className="au-label">Password</label>
                <div className="au-input-group">
                  <span className="au-input-icon">
                    <i className="fa fa-asterisk" aria-hidden="true" />
                  </span>
                  <input
                    type="text"
                    placeholder="Set a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="form-control"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || usernameStatus === "bad"}
              className={
                isSubmitting || usernameStatus === "bad"
                  ? "au-submit au-submit-disabled"
                  : "au-submit"
              }
            >
              {isSubmitting ? (
                <>
                  <span className="au-spinner" /> Adding User…
                </>
              ) : (
                <>
                  <i className="fa fa-user-plus" aria-hidden="true" /> Add User
                </>
              )}
            </button>
          </form>

          {submitPhase && (
            <div className="au-overlay" role="status" aria-live="polite">
              <div className="au-overlay-card">
                {submitPhase === "done" ? (
                  <>
                    <div className="au-tick">
                      <svg viewBox="0 0 52 52" width="64" height="64" aria-hidden="true">
                        <circle className="au-tick-circle" cx="26" cy="26" r="24" fill="none" />
                        <path className="au-tick-check" fill="none" d="M14 27 l8 8 l16 -16" />
                      </svg>
                    </div>
                    <div className="au-overlay-title">Member added</div>
                    <div className="au-overlay-sub">@{username} is now on your team</div>
                  </>
                ) : (
                  <>
                    <div className="au-orbit">
                      <span className="au-orbit-ring" />
                      <i className="fa fa-user-plus au-orbit-icon" aria-hidden="true" />
                    </div>
                    <div className="au-overlay-title">Adding {name || "new member"}</div>
                    <div className="au-overlay-sub">{SUBMIT_STEPS[submitStep]}</div>
                    <div className="au-steps">
                      {SUBMIT_STEPS.map((_, i) => (
                        <span
                          key={i}
                          className={i <= submitStep ? "au-step au-step-on" : "au-step"}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </Container>

      <style>{`
        .au-page { padding-bottom: 40px; }
        .au-card {
          margin: 24px auto;
          border-radius: 16px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 10px 30px rgba(20, 40, 70, 0.08);
          border: 1px solid #eef1f6;
          animation: au-fade-in 0.35s ease;
        }
        .au-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 18px 22px;
          background: linear-gradient(135deg, #2f8fc4, #1c6ba0);
          color: #fff;
          font-weight: 600;
          font-size: 1.05rem;
          cursor: pointer;
          user-select: none;
          transition: filter 0.15s ease;
        }
        .au-header:hover { filter: brightness(1.06); }
        .au-form { padding: 24px; max-width: 640px; margin: 0 auto; }
        .au-section {
          margin-bottom: 22px;
          padding-bottom: 18px;
          border-bottom: 1px dashed #e5e9f0;
          animation: au-fade-up 0.35s ease both;
        }
        .au-section:last-of-type { border-bottom: none; }
        .au-section-title {
          font-weight: 600;
          color: #1c6ba0;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.98rem;
        }
        .au-field { margin-bottom: 16px; }
        .au-field-row { display: flex; gap: 14px; }
        .au-field-row .au-field { flex: 1; min-width: 0; }
        .au-label { display: block; font-size: 0.82rem; color: #6b7686; margin-bottom: 6px; font-weight: 500; }
        .au-input-group { position: relative; display: flex; align-items: center; }
        .au-input-icon {
          position: absolute;
          left: 12px;
          color: #9aa5b5;
          display: flex;
          align-items: center;
          pointer-events: none;
        }
        .au-input-group .form-control,
        .au-input-group select.form-control {
          padding-left: 38px;
          padding-right: 34px;
          height: 44px;
          border-radius: 10px;
          border: 1px solid #dde3ec;
          transition: border-color 0.18s ease, box-shadow 0.18s ease;
        }
        .au-input-group .form-control:focus {
          border-color: #2f8fc4;
          box-shadow: 0 0 0 3px rgba(47, 143, 196, 0.15);
        }
        .au-input-readonly { background: #f6f8fb; color: #556; cursor: not-allowed; }
        .au-input-ok { border-color: #22c55e !important; }
        .au-input-ok:focus { box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15) !important; }
        .au-input-bad { border-color: #ef4444 !important; }
        .au-input-bad:focus { box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15) !important; }
        .au-input-trailing { position: absolute; right: 12px; font-size: 1.05rem; }
        .au-ok-pop { color: #22c55e; animation: au-pop 0.25s ease; }
        .au-bad-pop { color: #ef4444; animation: au-pop 0.25s ease; }

        .au-status { margin-top: 6px; font-size: 0.82rem; display: flex; align-items: center; gap: 6px; animation: au-fade-in 0.2s ease; }
        .au-status-ok { color: #178a45; }
        .au-status-bad { color: #d9362e; }
        .au-status-checking { color: #6b7686; }

        .au-preview {
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          border-radius: 10px;
          animation: au-fade-up 0.25s ease;
        }
        .au-preview-loading { color: #6b7686; font-size: 0.85rem; background: #f6f8fb; }
        .au-preview-bad { color: #d9362e; font-size: 0.85rem; background: #fdf1f0; }
        .au-preview-ok { background: #f2fbf5; border: 1px solid #d6f2e0; position: relative; }
        .au-preview-avatar {
          width: 36px; height: 36px; border-radius: 50%; object-fit: cover;
          border: 2px solid #fff; box-shadow: 0 0 0 1px #d6f2e0;
        }
        .au-preview-info { line-height: 1.2; }
        .au-preview-name { font-weight: 600; font-size: 0.88rem; color: #1a2b3c; }
        .au-preview-username { font-size: 0.78rem; color: #6b7686; }
        .au-preview-badge { margin-left: auto; color: #22c55e; }

        .au-submit {
          width: 100%;
          height: 48px;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #2f8fc4, #1c6ba0);
          color: #fff;
          font-weight: 600;
          font-size: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: transform 0.12s ease, filter 0.12s ease;
        }
        .au-submit:hover { filter: brightness(1.05); transform: translateY(-1px); }
        .au-submit:active { transform: translateY(0); }
        .au-submit-disabled { opacity: 0.65; cursor: not-allowed; transform: none; }

        .au-spinner, .au-spinner-sm {
          display: inline-block;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          animation: au-spin 0.7s linear infinite;
        }
        .au-spinner { width: 18px; height: 18px; }
        .au-spinner-sm {
          width: 14px; height: 14px;
          border: 2px solid rgba(47,143,196,0.25);
          border-top-color: #2f8fc4;
        }

        @keyframes au-spin { to { transform: rotate(360deg); } }
        @keyframes au-fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes au-fade-up { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes au-pop { 0% { transform: scale(0.4); opacity: 0; } 60% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes au-shake {
          10%, 90% { transform: translateX(-1px); }
          20%, 80% { transform: translateX(2px); }
          30%, 50%, 70% { transform: translateX(-4px); }
          40%, 60% { transform: translateX(4px); }
        }
        .au-shake { animation: au-shake 0.4s ease; }

        /* ---- submit overlay ---- */
        .au-overlay {
          /* Fixed, not absolute: the form is taller than most phone screens,
             so anchoring to the card would put the spinner off-screen for
             anyone who filled the form from the top. */
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(248, 250, 252, 0.86);
          backdrop-filter: blur(3px);
          -webkit-backdrop-filter: blur(3px);
          z-index: 1080;
          animation: au-fade-in 0.2s ease;
        }
        .au-overlay-card {
          text-align: center;
          padding: 28px 24px;
          max-width: 280px;
        }
        .au-overlay-title {
          margin-top: 18px;
          font-weight: 700;
          font-size: 1.02rem;
          color: #1a2b3c;
        }
        .au-overlay-sub {
          margin-top: 4px;
          font-size: 0.84rem;
          color: #6b7686;
          min-height: 1.2em;
          animation: au-fade-up 0.3s ease;
        }

        .au-orbit {
          position: relative;
          width: 64px;
          height: 64px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .au-orbit-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 3px solid rgba(47, 143, 196, 0.18);
          border-top-color: #2f8fc4;
          animation: au-spin 0.9s linear infinite;
        }
        .au-orbit-icon {
          color: #2f8fc4;
          font-size: 22px;
          animation: au-breathe 1.6s ease-in-out infinite;
        }

        .au-steps {
          display: flex;
          gap: 6px;
          justify-content: center;
          margin-top: 16px;
        }
        .au-step {
          width: 22px;
          height: 3px;
          border-radius: 2px;
          background: #dfe6ee;
          transition: background 0.3s ease;
        }
        .au-step-on { background: #2f8fc4; }

        .au-tick { display: flex; justify-content: center; }
        .au-tick-circle {
          stroke: #22c55e;
          stroke-width: 3;
          stroke-dasharray: 151;
          stroke-dashoffset: 151;
          animation: au-draw 0.4s ease forwards;
        }
        .au-tick-check {
          stroke: #22c55e;
          stroke-width: 4;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 40;
          stroke-dashoffset: 40;
          animation: au-draw 0.3s 0.32s ease forwards;
        }

        @keyframes au-draw { to { stroke-dashoffset: 0; } }
        @keyframes au-breathe {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50% { transform: scale(1.12); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .au-orbit-ring, .au-orbit-icon, .au-tick-circle, .au-tick-check,
          .au-overlay, .au-overlay-sub { animation: none; }
          .au-tick-circle, .au-tick-check { stroke-dashoffset: 0; }
        }

        @media (max-width: 480px) {
          .au-field-row { flex-direction: column; gap: 0; }
        }
      `}</style>
    </div>
  );
}
